import {
  type LongChatLevel,
  type NewChatSurface,
  formatSydneyDay,
  isBeforeSydneyToday,
  longChatLevel,
} from '../chat/threadLength';

import type { AskUIMessage } from './askUiMessage';

/** What GET /sessions/:id?wire=ui said about a reopened chat, kept after its history query closes. */
export interface LoadedSessionFacts {
  sessionId: string;
  /** Characters in the session's last model request, from the latest turn that recorded one. */
  contextChars?: number;
  /** session.updatedAt */
  updatedAt?: string;
  /** How many messages the payload held, so turns sent since can be told apart. */
  messageCount: number;
}

/** The newest assistant message's contextChars (the done event's metadata), if any carries one. */
export function latestMessageContextChars(messages: ReadonlyArray<AskUIMessage>): number | undefined {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const chars = messages[i].metadata?.contextChars;
    if (messages[i].role === 'assistant' && typeof chars === 'number') {
      return chars;
    }
  }
  return undefined;
}

/**
 * The chat's current size: a turn answered since the chat was opened wins, else the reopened
 * session's own value (which also counts a last turn that failed, whose done event never came).
 */
export function currentContextChars(
  messages: ReadonlyArray<AskUIMessage>,
  loaded: LoadedSessionFacts | null,
  sessionId: string | null,
): number | undefined {
  const forThisChat = loaded && loaded.sessionId === sessionId ? loaded : null;
  if (!forThisChat) {
    return latestMessageContextChars(messages);
  }
  return latestMessageContextChars(messages.slice(forThisChat.messageCount)) ?? forThisChat.contextChars;
}

function parseInstant(stamp: string | undefined): Date | undefined {
  const parsed = stamp ? Date.parse(stamp) : NaN;
  return Number.isFinite(parsed) ? new Date(parsed) : undefined;
}

export type LongChatNoticeState =
  | { level: 0 }
  | { level: Exclude<LongChatLevel, 0>; surface: NewChatSurface };

/**
 * Which long-chat notice to show. Never while an answer is working, never on a read-only chat
 * (a retired engine: only mantle chats continue), never without a chat or a new-chat action.
 * Every session this view opens is the reader's own (GET /sessions/:id is owner-only).
 */
export function longChatNoticeState(args: {
  contextChars: number | undefined;
  surface: NewChatSurface | null;
  sessionId: string | null;
  working: boolean;
  readOnly: boolean;
  snoozed: boolean;
}): LongChatNoticeState {
  const level = longChatLevel(args.contextChars);
  if (level === 0 || !args.surface || !args.sessionId || args.working || args.readOnly) {
    return { level: 0 };
  }
  if (level === 1 && args.snoozed) {
    return { level: 0 };
  }
  return { level, surface: args.surface };
}

/** The last time this chat was used: its newest stamped message, else the session's updatedAt. */
export function lastActivityOf(messages: ReadonlyArray<AskUIMessage>, loaded: LoadedSessionFacts): Date | undefined {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const stamp = parseInstant(messages[i].metadata?.createdAt);
    if (stamp) {
      return stamp;
    }
  }
  return parseInstant(loaded.updatedAt);
}

/**
 * "Continuing your chat from {day}": the Lens panel reopened the reader's last chat on its own,
 * that chat was last used before today (Sydney), and nothing has been asked in it since.
 */
export function resumedChatDay(args: {
  autoResumed: boolean;
  sessionId: string | null;
  readOnly: boolean;
  messages: ReadonlyArray<AskUIMessage>;
  loaded: LoadedSessionFacts | null;
  now: Date;
}): string | undefined {
  const { loaded } = args;
  if (!args.autoResumed || args.readOnly || !loaded || loaded.sessionId !== args.sessionId) {
    return undefined;
  }
  if (args.messages.length !== loaded.messageCount) {
    return undefined;
  }
  const last = lastActivityOf(args.messages, loaded);
  return last && isBeforeSydneyToday(last, args.now) ? formatSydneyDay(last) : undefined;
}
